// ─────────────────────────────────────────────────────────────────────────────
// ChatThreadsBar — CI-6 correction: iOS multi-chat shape on desktop.
//
// Port of mobile/rexy-ios/src/components/shared/ChatSidebar.tsx — a collapsible
// vertical bar (44px collapsed → 240px expanded) with chevron toggle + `+ NEW`
// button + thread list. Active row carries a 2px accent left-border (same as
// iOS). The desktop CRUD (rename/end/delete) is reached via a per-row `⋯`
// menu that appears on row hover — iOS doesn't expose those actions in the
// sidebar (single-user, simpler model) so they live behind a hover affordance
// on desktop without cluttering the minimal default.
//
// FUNCTION: untouched. Every handler is a pass-through from App.tsx (via
// ChatSurface). No state moves out of App.tsx; this component holds only a
// local `menuOpenId` for the per-row ⋯ popup, which is purely UI.
//
// ChatThreads.tsx (the desktop drawer) stays in the codebase untouched; this
// new component is mounted by ChatSurface in its place.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Thread } from '../ChatThreads';

const COLLAPSED_WIDTH = 44;
const EXPANDED_WIDTH = 240;

export interface ChatThreadsBarProps {
  threads: Thread[];
  activeThreadId: string | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSelectThread: (id: string) => void;
  onNewThread: () => Promise<void> | void;
  onEndThread: (id: string) => Promise<void> | void;
  onRenameThread: (id: string, name: string) => Promise<void> | void;
  onDeleteThread: (id: string) => Promise<void> | void;
  accentColor: string;
}

export default function ChatThreadsBar({
  threads,
  activeThreadId,
  collapsed,
  onToggleCollapse,
  onSelectThread,
  onNewThread,
  onEndThread,
  onRenameThread,
  onDeleteThread,
  accentColor,
}: ChatThreadsBarProps) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  return (
    <div
      className="flex flex-col h-full overflow-hidden border-r transition-[width]"
      style={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        // Translucent surface — atmosphere tones through (matches the lifted
        // input bar treatment; consistent across the chat chrome).
        background: 'rgba(8, 8, 24, 0.45)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRightColor: 'rgba(255,255,255,0.06)',
      }}
    >
      {/* Header — chevron toggle + (when expanded) NEW button */}
      <div
        className="flex items-center gap-2 px-2 py-2.5 border-b"
        style={{ borderBottomColor: 'rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={onToggleCollapse}
          className="w-7 h-7 rounded-full flex items-center justify-center
            text-rexy-text hover:bg-white/[0.06] transition-colors"
          title={collapsed ? 'Expand threads' : 'Collapse threads'}
          aria-label={collapsed ? 'Expand threads' : 'Collapse threads'}
        >
          <span className="text-base leading-none">{collapsed ? '›' : '‹'}</span>
        </button>

        {!collapsed && (
          <button
            onClick={() => onNewThread()}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl
              border bg-transparent text-[10px] font-bold uppercase
              tracking-widest transition-colors hover:bg-white/[0.04]"
            style={{ borderColor: accentColor, color: accentColor }}
            title="New thread"
          >
            <span className="text-sm leading-none">+</span>
            <span>NEW</span>
          </button>
        )}
      </div>

      {/* Thread list — only shown when expanded */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-1">
          {threads.length === 0 && (
            <div className="text-center text-rexy-muted text-xs py-6 italic">
              No threads yet
            </div>
          )}
          {threads.map((t) => {
            const isActive = t.id === activeThreadId;
            const displayName = t.name || 'New conversation';
            return (
              <div
                key={t.id}
                className="group relative flex items-center pr-1 transition-colors hover:bg-white/[0.03]"
                style={{
                  borderLeft: '2px solid',
                  borderLeftColor: isActive ? accentColor : 'transparent',
                }}
              >
                <button
                  onClick={() => onSelectThread(t.id)}
                  className="flex-1 text-left px-3 py-2.5 min-w-0"
                  title={displayName}
                >
                  <div
                    className={`text-[13px] truncate ${
                      isActive ? 'text-rexy-text font-medium' : 'text-rexy-muted'
                    }`}
                  >
                    {displayName}
                  </div>
                  {t.status === 'closed' && (
                    <div className="text-[9px] text-rexy-muted/60 mt-0.5 uppercase tracking-widest">
                      Closed
                    </div>
                  )}
                </button>

                {/* Per-row ⋯ menu — hover-revealed (group-hover); preserves
                    rename/end/delete CRUD without cluttering the default look. */}
                <div
                  className="relative opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setMenuOpenId(menuOpenId === t.id ? null : t.id)}
                    className="w-6 h-6 rounded-full flex items-center justify-center
                      text-rexy-muted hover:text-rexy-text hover:bg-white/[0.06] transition-colors"
                    title="Thread actions"
                  >
                    <span className="text-base leading-none">⋯</span>
                  </button>
                  {menuOpenId === t.id && (
                    <div className="absolute top-full right-0 mt-1 bg-rexy-card border border-rexy-border
                      rounded-lg shadow-xl z-50 min-w-[140px] overflow-hidden">
                      <button
                        onClick={() => {
                          setMenuOpenId(null);
                          const next = window.prompt('Rename thread:', t.name || '');
                          if (next && next.trim()) onRenameThread(t.id, next.trim());
                        }}
                        className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                          tracking-widest text-rexy-muted hover:text-rexy-text hover:bg-rexy-border transition-colors"
                      >
                        Rename
                      </button>
                      {t.status === 'active' && (
                        <button
                          onClick={() => { setMenuOpenId(null); onEndThread(t.id); }}
                          className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                            tracking-widest text-rexy-muted hover:text-rexy-text hover:bg-rexy-border transition-colors"
                        >
                          End thread
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMenuOpenId(null);
                          if (window.confirm(`Delete thread "${t.name || t.id}"?`)) {
                            onDeleteThread(t.id);
                          }
                        }}
                        className="w-full text-left px-3 py-2 text-[10px] font-bold uppercase
                          tracking-widest text-rexy-muted hover:text-red-400 hover:bg-red-900/20 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
